import EventTaskDatesPopup from '../../../../events/new_event/EventTaskDatesPopup.vue';
import GoalTaskStepsPopup from './GoalTaskStepsPopup.vue';
import notifications from '~/vue/plugins/notifications';
import { loadingSpinner } from '~/vue/plugins/vueLoadingSpinner';
import urls from 'DjangoUrls';
import _ from 'lodash';
import { customTermFilter } from '~/vue/filters/CustomTerm';
import { selectFactory } from '~/angular/app/shared/select_factory/select_factory';
import GoalResource from '~/angular/app/projects/project_resource';
import { simplePopupFactory } from '~/angular/app/shared/simple_popups/simplePopupFactory';
import { createDeferred } from 'shared/utils/deferred';
import addMedia from '~/angular/app/add_media/addMedia';
import GoalTaskMainOptionsPopup
    from '~/angular/app/projects/GoalDetailPage/goal_tasks/goal_task_options/GoalTaskMainOptionsPopup.vue';

let { DjangoUrls } = urls;

const DEFAULT_GOAL_TASK_DATA = {
    name: '',
    description: '',
    assignee: undefined,
    start_date: undefined,
    end_date: undefined,
    notify_when_due: undefined,
    auto_complete_when_due: undefined,
    media_for_avatar: undefined,
    avatar: ''
};

// shared between the wizard steps: main_task_options fills them in, the wizard reads them back
let loadedTaskData = {};

let task_to_copy_id = undefined;

let target_goal_id = undefined;

function show_add_media_menu(default_callback, cancel_callback) {
    addMedia.add_media(
        {
            allow_add_from_library: true,
            max_count: 1,
            type: 'image',
            popup_header: 'What sort of media would you like to add?'
        },
        {
            default_callback,
            cancel_callback
        }
    );
}

function show_set_assignee_menu(goal_id = undefined, popup_config = {}, allow_multiple = false) {
    const url = DjangoUrls['api.v1:goals-get-goals-members-list-for-action-link'](goal_id || target_goal_id);

    return selectFactory.endpoint_selector(url, allow_multiple, {
        header: popup_config.header || 'Select assignee',
        info: (popup_config.info !== undefined) ? popup_config.info : `Select or de-
        select a user
        from list, or leave this
        if you do not want to assign this ${customTermFilter('Task')}.`,
        button_yes: popup_config.save_button || 'Update',
        button_no: popup_config.cancel_button || 'Cancel',
        additional_filters: popup_config.additional_filters || [],
        empty: 'You do not have any items to select'
    }, !allow_multiple);
}

function show_copy_task_menu() {
    const res = createDeferred();
    let selectedGoalId;
    selectFactory.endpoint_selector(GoalResource().get_goals_to_copy_task, false, {
        header: `Select ${customTermFilter('Goal')} to copy ${customTermFilter('Task')}`,
        info: `Select ${customTermFilter('Goal')}
               from which you want to copy ${customTermFilter('Task')}.`,
        button_yes: 'Select',
        button_no: 'Cancel',
        empty: `You do not have any ${customTermFilter('Goal')} to select`
    }, false)
        .then(goalId => {
            selectedGoalId = goalId;
            return selectFactory.endpoint_selector(_.partial(GoalResource().get_actions_to_copy, goalId), false, {
                header: `Select ${customTermFilter('Task')} to copy data`,
                info: `Select ${customTermFilter('Task')} to copy data to new one.`,
                button_yes: 'Select',
                button_no: 'Cancel',
                empty: `You do not have any ${customTermFilter('Tasks')} created by you in that ${customTermFilter('Goal')}.`
            }, false);
        }, err => {
            res.reject();
            return new Promise(_.noop);
        })
        .then(
            taskId => {
                loadingSpinner.show();
                return GoalResource().get_task_data_to_copy(selectedGoalId, taskId);
            },
            (cancel) => {
                res.reject();
                return new Promise(_.noop);
            }
        )
        .then(resp => {
            res.resolve(resp.data);
        }, err => {
            notifications.error(err || 'Error');
            res.reject();
            return new Promise(_.noop);
        })
        .finally(() => loadingSpinner.hide());
    return res.promise;
}

/**
 * @param initial {Object}
 * {name:string,
 * description: string,
 * avatar: string?,
 * auto_avatar: Boolean,
 * media_for_avatar: MediaContent
 * }
 * @param popup_config {Object}
 * @param promise_to_resolve {Object} deferred to settle instead of a fresh one.
 * Set when the popup reopens itself (media added, another task loaded), so that every
 * reopened popup still settles the promise the first call returned.
 * @return {Promise} resolves with the object above but filled\changed, rejects if cancelled
 */
function main_task_options(initial, popup_config = {}, promise_to_resolve) {
    let DEFAULT_POPUP_CONFIG = {
        header: `Edit ${customTermFilter('Goal')} ${customTermFilter('Task')}`,
        save_button: 'Save',
        cancel_button: 'Cancel',
        allow_clone: false
    };
    let actual_popup_config = _.merge({}, DEFAULT_POPUP_CONFIG, popup_config);
    let initial_data = _.merge({}, initial);
    const options_result = promise_to_resolve === undefined ? createDeferred() : promise_to_resolve;

    const {
        popupComponent,
        vm,
        fEl
    } = simplePopupFactory.mount_vue_popup(GoalTaskMainOptionsPopup, {}, {}, {
        header: actual_popup_config.header,
        allowQuickCreate: actual_popup_config.allow_quick_create,
        allowClone: actual_popup_config.allow_clone,
        saveButton: actual_popup_config.save_button,
        cancelButton: actual_popup_config.cancel_button,
    });
    const popup_result = popupComponent.show(
        initial_data
    );

    let add_media_items = (items_list) => {
        initial_data.media_for_avatar = items_list[0];
        main_task_options(initial_data, popup_config, options_result);
    };

    popup_result.then(res => {
        if (res.type === 'add_media') {
            initial_data = res.data;
            show_add_media_menu(add_media_items, () => {
                main_task_options(initial_data, popup_config, options_result);
            });
        }
        if (res.type === 'load_another_task') {
            initial_data = res.data;
            show_copy_task_menu()
                .then(taskData => {
                    loadedTaskData = taskData;
                    task_to_copy_id = taskData.id;
                    main_task_options(taskData, popup_config, options_result);
                }, cancel => {
                    main_task_options(initial_data, popup_config, options_result);
                });
        }
        if (res.type === 'save') {
            options_result.resolve(res.data);
        }
    }, () => options_result.reject()).finally(() => {
        vm?.$destroy();
        fEl?.focus();
    });

    return options_result.promise;
}

function steps_options() {
    const {
        popupComponent,
        vm
    } = simplePopupFactory.mount_vue_popup(GoalTaskStepsPopup);

    return popupComponent.show()
        .finally(() => {
            if (vm) {
                vm.$destroy();
            }
        });
}

function date_options(initial, popup_config = {}) {
    let initial_data = _.merge({}, DEFAULT_GOAL_TASK_DATA, initial);
    let DEFAULT_POPUP_CONFIG = {
        header: `Edit ${customTermFilter('Goal')} ${customTermFilter('Task')} Dates`,
        save_button: 'Save',
        cancel_button: 'Cancel',
        allow_create_steps: false,
    };
    let actual_popup_config = _.merge({}, DEFAULT_POPUP_CONFIG, popup_config);

    return new Promise((resolve, reject) => {
        const {
            popupComponent,
            vm
        } = simplePopupFactory.mount_vue_popup(EventTaskDatesPopup);
        popupComponent.show(undefined, initial_data, actual_popup_config)
            .then(res => {
                resolve(res);
            }, () => reject())
            .finally(() => {
                if (vm) {
                    vm.$destroy();
                }
            });
    });
}

function assignee_options(goal_id, popup_config = {}, allow_multiple = false) {
    return new Promise((resolve, reject) => {
        show_set_assignee_menu(goal_id, popup_config, allow_multiple).then(selected_user_id => {
            resolve(selected_user_id);
        }, () => reject());
    });
}

/**
 * Runs the create-{Task} wizard: main options -> dates -> steps -> assignee.
 * Pressing "Create" in the first popup (createNow) skips the scheduling steps,
 * and the steps popup is only shown if the dates popup asked for it.
 *
 * @return {Promise} resolves with the data to create the task with.
 * Cancelling any step aborts the wizard: the promise rejects and no further popup is shown.
 */
async function show_create_goal_task_wizard(goal_id, allow_select_assignee = true, dateOptionsFunction = date_options, allow_quick_create = true) {
    target_goal_id = goal_id;
    loadedTaskData = {};
    task_to_copy_id = undefined;

    let {
        name,
        description,
        media_for_avatar,
        avatar,
        createNow
    } = await main_task_options(_.merge({}, DEFAULT_GOAL_TASK_DATA), {
        header: `Create new ${customTermFilter('Task')}`,
        save_button: 'Next',
        allow_clone: true,
        allow_quick_create
    });

    let start_date, end_date, notify_when_due, auto_complete_when_due, linkToEvent, createSteps = false;
    if (!createNow) {
        // loadedTaskData is what main_task_options copied from another task, if any
        ({
            start_date,
            end_date,
            notify_when_due,
            auto_complete_when_due,
            linkToEvent,
            createNow,
            createSteps
        } = await dateOptionsFunction(loadedTaskData, {
            header: `Schedule ${customTermFilter('Task')}?`,
            save_button: 'Next',
            cancel_button: 'Cancel',
            message: `Add in start and end dates to schedule your ${customTermFilter('Task')}.`,
            allow_create_steps: true,  // only from wizard perhaps
        }));
    }

    let stepsToSave;
    if (!createNow && createSteps) {
        const steps = await steps_options();
        if (steps && steps.length) {
            stepsToSave = steps;
        }
    }

    let assignee_id;
    if (allow_select_assignee) {
        assignee_id = await assignee_options(undefined, {
            header: `Select assignee for ${customTermFilter('Task')}?`,
            save_button: `Create ${customTermFilter('Task')}`,
            cancel_button: 'Cancel'
        });
    }

    return {
        name,
        description,
        media_id_for_avatar: media_for_avatar ? media_for_avatar.pk : null,
        reset_avatar: !media_for_avatar && !avatar,
        start_date,
        end_date,
        notify_when_due,
        auto_complete_when_due,
        assignee_id,
        task_to_copy_id,
        linkToEvent,
        steps: stepsToSave,
    };
}

function edit_main_options(goal_task) {
    const promise = createDeferred();
    const goal_task_data = _.merge({}, DEFAULT_GOAL_TASK_DATA, goal_task);
    main_task_options(goal_task_data).then(res => {
        promise.resolve(res);
    }, () => {
        // cancelled: the returned promise is never settled
    });
    return promise.promise;
}

function edit_date_options(goal_task) {
    return date_options(goal_task);
}

const goalTaskFactory = {
    show_create_goal_task_wizard,
    edit_main_options,
    edit_date_options,
    assignee_options
};

export { goalTaskFactory };
